import { useState, useCallback } from 'react';
import { useAuth } from '../provider/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Build URL with query parameters
 * @param {string} endpoint - Base endpoint
 * @param {object} params - Query parameters
 * @returns {string} Full URL with query string
 */
function buildUrl(endpoint, params = {}) {
  const url = `${API_URL}${endpoint}`;
  
  // If no params, return base URL
  if (!params || Object.keys(params).length === 0) {
    return url;
  }
  
  // Build query string
  const queryString = new URLSearchParams(
    Object.entries(params)
      .filter(([_, value]) => value !== null && value !== undefined)
      .map(([key, value]) => [key, String(value)])
  ).toString();
  
  return queryString ? `${url}?${queryString}` : url;
}

/**
 * Make authenticated API request to FastAPI backend
 * 
 * @param {string} endpoint - API endpoint (e.g., '/users', '/items/123')
 * @param {object} options - Request options
 * @param {string} options.method - HTTP method (GET, POST, PUT, DELETE)
 * @param {object} options.params - URL query parameters
 * @param {object} options.body - Request body data
 * @param {object} options.headers - Additional headers
 * @param {string} token - JWT token from Keycloak
 * @returns {Promise} Response data
 */
export async function apiRequest(endpoint, options = {}, token = null) {
  const {
    method = 'GET',
    params = {},
    body = null,
    headers: customHeaders = {},
    binary = false,
  } = options;

  const headers = {
    'Content-Type': 'application/json',
    ...customHeaders,
  };

  // Add token if provided
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Build URL with query parameters
  const url = buildUrl(endpoint, params);

  // Build fetch options
  const fetchOptions = {
    method,
    headers,
  };

  // Add body for POST/PUT/PATCH requests
  if (body && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
    fetchOptions.body = JSON.stringify(body);
  }

  try {
    
    const response = await fetch(url, fetchOptions);

    // Handle different status codes
    if (response.status === 401) {
      throw new Error('Unauthorized - Please login again');
    }

    if (response.status === 403) {
      throw new Error('Forbidden - You do not have permission');
    }

    if (response.status === 404) {
      throw new Error('Resource not found');
    }

    if (response.status === 422) {
      const errorData = await response.json();
      throw new Error(`Validation Error: ${JSON.stringify(errorData.detail)}`);
    }

    if (response.status === 503) {
      throw new Error('Authentication service unavailable');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `API Error: ${response.status}`);
    }

    if (binary) {
      return await response.blob();
    }

    // Check if response has content before trying to parse JSON
    const text = await response.text();

    // Handle empty responses (common for DELETE operations)
    if (!text || text.trim() === '') {
      return { success: true };
    }

    // Try to parse as JSON
    try {
      return JSON.parse(text);
    } catch (e) {
      console.error('Failed to parse response as JSON:', text);
      // If parsing fails but response was successful, return success
      return { success: true };
    }
  } catch (error) {
    console.error('API Request failed:', error);
    throw error;
  }
}

// Convenience methods for common HTTP methods
export const api = {
  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {object} params - Query parameters
   * @param {string} token - JWT token
   */
  get: (endpoint, params = {}, token) => 
    apiRequest(endpoint, { method: 'GET', params }, token),

  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {object} params - Query parameters
   * @param {string} token - JWT token
   */
  getBinary: (endpoint, params = {}, token) => 
    apiRequest(endpoint, { method: 'GET', params, binary: true }, token),
  
  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {object} body - Request body
   * @param {object} params - Query parameters (optional)
   * @param {string} token - JWT token
   */
  post: (endpoint, body = {}, params = {}, token) => 
    apiRequest(endpoint, { method: 'POST', body, params }, token),
  
  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {object} body - Request body
   * @param {object} params - Query parameters (optional)
   * @param {string} token - JWT token
   */
  put: (endpoint, body = {}, params = {}, token) => 
    apiRequest(endpoint, { method: 'PUT', body, params }, token),
  
  /**
   * PATCH request
   * @param {string} endpoint - API endpoint
   * @param {object} body - Request body
   * @param {object} params - Query parameters (optional)
   * @param {string} token - JWT token
   */
  patch: (endpoint, body = {}, params = {}, token) => 
    apiRequest(endpoint, { method: 'PATCH', body, params }, token),
  
  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @param {object} params - Query parameters (optional)
   * @param {string} token - JWT token
   */
  delete: (endpoint, params = {}, token) => 
    apiRequest(endpoint, { method: 'DELETE', params }, token),
};

export function useApi(apiFn, minLoadingTime = 0) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = async (...args) => {
    setLoading(true);
    setError(null);

    const startTime = Date.now();

    try {
      const result = await apiFn({ token, ...args[0] });

      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      setData(result);
      return result;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, error, execute };
}